import axios from 'axios'
import { AssistantEnums } from './assitants-enums'
import store from '../../store'

export const assistantReadThread = async (thread_id) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      let assistant_response = null

      try {
        if (!thread_id) {
          reject(new Error('Thread ID is required for reading thread content'))
          return
        }

        // Validate thread_id format
        if (!thread_id.startsWith('thread_')) {
          reject(new Error(`Invalid thread_id format: ${thread_id}. Expected an ID that begins with 'thread'.`))
          return
        }

        // Use our backend endpoint to read thread messages
        assistant_response = await axios.post(AssistantEnums.routes.READ_THREAD, {
          thread_id,
          limit: 100,
          order: 'desc' // Get newest messages first
        })

        if (!assistant_response || !assistant_response.data) {
          reject(new Error('Empty response when reading thread content'))
          return
        }

        resolve(assistant_response)
      } catch (e) {
        console.error('Error reading thread content:', e)

        // Provide more detailed error information
        const errorMessage = e.response?.data?.error || e.message || 'Unknown error'
        const statusCode = e.response?.status || 'No status'
        reject(new Error(`Error reading thread (${statusCode}): ${errorMessage}`))
      }
    })()
  })
}

export const assistantInitiate = async (newMessage, assistant_id, context = null) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      let response = null

      try {
        if (!store.getState().auth || !store.getState().auth.agiliteUser || !store.getState().auth.agiliteUser._id) {
          reject(new Error('User authentication required'))
          return
        }

        if (!store.getState().core || !store.getState().core.entity || !store.getState().core.entity._id) {
          reject(new Error('Entity reference required'))
          return
        }

        if (!assistant_id) {
          reject(new Error('Assistant ID is required'))
          return
        }

        if (!newMessage) {
          reject(new Error('Message content is required'))
          return
        }

        const userRef = store.getState().auth.agiliteUser._id
        const entityRef = store.getState().core.entity._id

        // Format message with context if provided and not already formatted
        let messageToSend = newMessage
        if (context && !newMessage.includes('Context Information:')) {
          const contextStr = typeof context === 'string' ? context : JSON.stringify(context, null, 2)

          messageToSend = `
Context Information:
\`\`\`json
${contextStr}
\`\`\`

User Message:
${newMessage}
`
        }

        response = await axios.post(AssistantEnums.routes.INITIATE_THREAD, {
          entityRef,
          userRef,
          assistant_id,
          message: messageToSend,
          context: context // Also pass the raw context in case the backend wants to handle it differently
        })

        if (!response || !response.data) {
          reject(new Error('Empty response when initiating thread'))
          return
        }

        if (!response.data.thread_id || !response.data.id) {
          console.error('Invalid response format:', response.data)
          reject(new Error('Invalid response format: missing thread_id or run_id'))
          return
        }

        resolve(response.data)
      } catch (e) {
        console.error('Error initiating thread:', e)

        // Provide more detailed error information
        const errorMessage = e.response?.data?.error || e.message || 'Unknown error'
        const statusCode = e.response?.status || 'No status'
        reject(new Error(`Error initiating thread (${statusCode}): ${errorMessage}`))
      }
    })()
  })
}

export const assistantContinue = async (newMessage, thread_id, assistant_id) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      let response = null

      try {
        if (!thread_id) {
          reject(new Error('Thread ID is required'))
          return
        }

        // Validate thread_id format
        if (!thread_id.startsWith('thread_')) {
          reject(new Error(`Invalid thread_id format: ${thread_id}. Expected an ID that begins with 'thread'.`))
          return
        }

        if (!newMessage) {
          reject(new Error('Message content is required'))
          return
        }

        // Use our backend endpoint to continue the thread
        try {
          response = await axios.post(AssistantEnums.routes.CONTINUE_THREAD, {
            thread_id,
            message: newMessage,
            assistant_id: assistant_id ? assistant_id : AssistantEnums.assistant_ids.MEDICAL_CHATBOT
          })
        } catch (error) {
          console.error('Error continuing thread:', error)
          throw error
        }

        if (!response || !response.data) {
          reject(new Error('Empty response when continuing thread'))
          return
        }

        if (!response.data.id) {
          console.error('Invalid response format:', response.data)
          reject(new Error('Invalid response format: missing run_id'))
          return
        }

        resolve({
          thread_id,
          id: response.data.id
        })
      } catch (e) {
        console.error('Error continuing thread:', e)

        // Provide more detailed error information
        const errorMessage = e.response?.data?.error?.message || e.response?.data?.error || e.message || 'Unknown error'
        const statusCode = e.response?.status || 'No status'
        const errorDetails = e.response?.data?.details || ''

        console.error('Error details:', {
          message: errorMessage,
          status: statusCode,
          details: errorDetails,
          response: e.response?.data
        })

        reject(
          new Error(
            `Error continuing thread (${statusCode}): ${errorMessage}${errorDetails ? ` - ${errorDetails}` : ''}`
          )
        )
      }
    })()
  })
}

export const assistantCheckRunStatus = async (thread_id, run_id) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      let response = null

      try {
        if (!thread_id) {
          reject(new Error('Thread ID is required'))
          return
        }

        // Validate thread_id format
        if (!thread_id.startsWith('thread_')) {
          reject(new Error(`Invalid thread_id format: ${thread_id}. Expected an ID that begins with 'thread'.`))
          return
        }

        if (!run_id) {
          reject(new Error('Run ID is required'))
          return
        }

        response = await axios.post(AssistantEnums.routes.CHECK_THREAD_STATUS, {
          thread_id,
          run_id
        })

        if (!response || !response.data) {
          reject(new Error('Empty response when checking run status'))
          return
        }

        if (!response.data.status) {
          console.error('Invalid response format:', response.data)
          reject(new Error('Invalid response format: missing status'))
          return
        }

        resolve(response.data)
      } catch (e) {
        console.error('Error checking run status:', e)

        // Provide more detailed error information
        const errorMessage = e.response?.data?.error || e.message || 'Unknown error'
        const statusCode = e.response?.status || 'No status'
        reject(new Error(`Error checking run status (${statusCode}): ${errorMessage}`))
      }
    })()
  })
}
